import {
  ExpressFormMenuKey,
  getExpressSectionSourceDataByKey,
} from "@/pages/ExpressForm/express-data";
import { isValidUUID } from "@/utils/Functions";
import {
  Account,
  Person,
  ServiceProvider,
  NotifierRoles,
  UploadedDocument,
  DocumentsPersistedState,
  NonExpressFormData,
  DocumentIds,
  DocumentErrors,
  DocumentsRecord,
  UIQuilterState,
  AccountNextStep,
  InternalFileTags,
  FileTags,
} from "@/types";
import {
  FILE_NAME_REGEX,
  ACCEPTABLE_MIME_TYPES,
  MAX_FILE_SIZE,
  MIME,
  PDF_MIME,
  READABLE_MAX_FILE_SIZE_NON_PDF,
  READABLE_MAX_FILE_SIZE_PDF,
} from "../utils/Constants";
import { ReactStateSetter } from "@/utils/Types";
import { Section } from "@/Sections";
import { UpdateFormTemplateProc } from "@/templates/FormTemplate";
import { isSolicitorOrFinancialAdvisor } from "./person-helpers";

// either service provider needs a will or role in executor or solicitor will is required
export const isWillRequired = (
  accounts: ReadonlyArray<Account> | undefined,
  notifier: Person | undefined,
  spMap: Record<string, ServiceProvider>,
  intestacyFlow: boolean,
  collectWill: boolean
): boolean => {
  if (intestacyFlow) return collectWill;
  return !!accounts?.some(
    (account: Account) => account.companyId && spMap[account.companyId]?.willNeeded
  );
};

export const isWillRequiredExpressForm = (
  serviceProvider: ServiceProvider,
  collectWill: boolean
) => {
  if (serviceProvider.intestacyFlow) return collectWill;
  // sp does not required will
  if (!serviceProvider.willNeeded) return false;
  const notifierInfo: Person | undefined = getExpressSectionSourceDataByKey(
    ExpressFormMenuKey.Notifier
  );
  // notifier details are not provided so will is required
  if (!notifierInfo) return true;

  return true;
};

export function sanitizeFileName(filename: string): string {
  let sanitized = filename.trim();
  sanitized = sanitized.replace(FILE_NAME_REGEX, "");

  sanitized = sanitized.slice(0, 24);

  return sanitized.replace(/\.+/g, ".").replace(/\/+/g, "").replace(/\\+/g, "");
}

export function isAllowedMimeType(mimeType: string): boolean {
  return ACCEPTABLE_MIME_TYPES.includes(mimeType.toLowerCase());
}

export function isValidSize(size: number): boolean {
  if (size > MAX_FILE_SIZE) return false;
  return true;
}

export function isCompressableType(file: File): boolean {
  // return COMPRESSABLE_MIME_TYPES.includes(file.type);
  return true;
}
export function fileSizeErrorByMime(file_type: MIME | string): string {
  let size: string = READABLE_MAX_FILE_SIZE_NON_PDF;
  if (file_type === PDF_MIME) {
    size = READABLE_MAX_FILE_SIZE_PDF;
  }

  return `File uploaded is larger than ${size}, please upload files that are within ${size} / as large as ${size}`;
}

export function compress(file: File): Promise<File> {
  return Promise.resolve(file);
  // if (!isCompressableType(file)) {
  //   console.log("Compression did not occur (type not supported)");
  //   return Promise.resolve(file);
  // }

  // return new Promise((resolve, _reject) => {
  //   new Compressor(file, {
  //     mimeType: "image/jpeg",
  //     success: (compressed: File) => {
  //       console.log("Compressed file:", compressed.type, compressed.size / 1024);
  //       resolve(compressed);
  //     },
  //     error: (err: any) => {
  //       console.log("Compression failed", err);
  //       resolve(file);
  //     },
  //   });
  // });
}

export function isPOBARequiredExpress(
  quilter: UIQuilterState,
  notifierRole: NotifierRoles | undefined | null
): boolean {
  return (
    // DO NOT DISPLAY FOR Solicitor OR FINANCIAL ADVISOR
    !isSolicitorOrFinancialAdvisor(notifierRole) &&
    // IF “Withdrawal of funds to an Executor account” is selected.
    quilter[AccountNextStep.WithdrawalOfFundsToExecutorAccount]
  );
}

export function isProofOfBankAccountRequired(
  accounts: ReadonlyArray<Account> | undefined,
  quilter: UIQuilterState,
  notifierRole: NotifierRoles | undefined | null
): boolean {
  if (!notifierRole || !accounts || accounts.length === 0) return false;
  return (
    // DO NOT DISPLAY FOR Solicitor OR FINANCIAL ADVISOR
    !isSolicitorOrFinancialAdvisor(notifierRole) &&
    // IF “Withdrawal of funds to an Executor account” is selected.
    quilter[AccountNextStep.WithdrawalOfFundsToExecutorAccount]
  );
}

// Quilter no longer requires any documents - all 4x (death certificate, will,
// grant of representation, proof of address) are always optional.
export function isGrantOfRepresentationRequired(
  _quilter: UIQuilterState,
  spsRequireGOR: boolean
): boolean {
  //  if (quilter.isQuilter) {
  //   // Yes IF Probate Threshold is over £100k (or £30k offshore)
  //   return (
  //     (quilter.isPlaceOfDeathUk && quilter.quilterThresholdReached && spsRequireGOR) ||
  //     (!quilter.isPlaceOfDeathUk && spsRequireGOR)
  //   );
  // }
  return spsRequireGOR;
}

export function isGOROptional(_quilter: UIQuilterState, spsRequireGOR: boolean): boolean {
  // if (quilter.isQuilter) {
  //   return quilter.isPlaceOfDeathUk && !quilter.quilterThresholdReached && spsRequireGOR;
  // }
  return !spsRequireGOR;
}
export const documentsPersistedStateFromForm = (
  form: NonExpressFormData
): DocumentsPersistedState => {
  // Filter out invalid UUIDs to prevent backend errors
  const proofOfAddress = (form.documents?.proofOfAddressIds || []).filter(isValidUUID);
  const proofOfDeath = (form.documents?.proofOfDeathIds || []).filter(isValidUUID);
  const willAndProbate = (form.documents?.willAndProbateIds || []).filter(isValidUUID);
  const grantOfRepresentation = (form.documents?.grantOfRepresentationIds || []).filter(
    isValidUUID
  );
  const proofOfBankAccount = (form.documents?.proofOfBankAccountIds || []).filter(isValidUUID);

  return {
    record: {
      proofOfAddress: {
        originalIds: proofOfAddress,
        currentIds: proofOfAddress,
        previousDocuments: [],
        files: [],
      },
      proofOfDeath: {
        originalIds: proofOfDeath,
        currentIds: proofOfDeath,
        previousDocuments: [],
        files: [],
      },
      willAndProbate: {
        originalIds: willAndProbate,
        currentIds: willAndProbate,
        previousDocuments: [],
        files: [],
      },
      grantOfRepresentation: {
        originalIds: grantOfRepresentation,
        currentIds: grantOfRepresentation,
        previousDocuments: [],
        files: [],
      },
      proofOfBankAccount: {
        originalIds: proofOfBankAccount,
        currentIds: proofOfBankAccount,
        previousDocuments: [],
        files: [],
      },
    },
    hasChanges: false,
    errors: {},
    refreshPreviousDocuments: 0,
  };
};

export const updatedFormFromPersistedState = (
  form: NonExpressFormData,
  newIds: DocumentIds
): NonExpressFormData => {
  const documents = {
    proofOfAddressIds: newIds.proofOfAddress,
    proofOfDeathIds: newIds.proofOfDeath,
    willAndProbateIds: newIds.willAndProbate,
    grantOfRepresentationIds: newIds.grantOfRepresentation,
    proofOfBankAccountIds: newIds.proofOfBankAccount,
  };

  return {
    ...form,
    documents: documents,
  };
};

export const serverRecordFromPersistedState = (_: DocumentsPersistedState, newIds: DocumentIds) => {
  return {
    proofOfAddressIds: newIds.proofOfAddress,
    deathCertificateIds: newIds.proofOfDeath,
    willAndProbateIds: newIds.willAndProbate,
    grantOfRepresentationIds: newIds.grantOfRepresentation,
    proofOfBankAccountIds: newIds.proofOfBankAccount,
  };
};

export interface IDocumentsProps {
  caseId: string;
  signature: string | null;
  prepareUpdate: (section: Section, form: NonExpressFormData) => NonExpressFormData;
  updateSuccessful: (form: NonExpressFormData, section: Section, justSave?: boolean) => void;
  updateFailure: (error: Error) => void;
  serviceProvidersMap: Record<string, ServiceProvider>;
  form: NonExpressFormData;
  busy: boolean;
  continueWithoutChanges: (section: Section, doNotAdvance?: boolean) => void;
  remoteError: string | undefined;
  uploadFile: (file: File, tags: ReadonlyArray<FileTags>) => Promise<UploadedDocument>;
  uploadedFileInfo: (id: string) => Promise<Document>;
  persistedState: DocumentsPersistedState;
  setPersistedState: ReactStateSetter<DocumentsPersistedState>;
  onSectionClick: (section: string) => void;
  updateTemplate: UpdateFormTemplateProc;
  removeRemoteError: () => void;
}

export interface IDocumentsView {
  askForProofOfAddress?: boolean;
  askForWillAndProbate?: boolean;
  askForGrantOfRepresentation?: boolean;
  askForProofOfBankAccount?: boolean;
  busy: boolean;
  record: DocumentsRecord;
  errors: any;
  hasChanges: boolean;
  remoteError?: string;
  uploadedFileInfo: any;
  setError: any;
  updateProofOfAddress: any;
  updateProofOfDeath: any;
  updateWillAndProbate: any;
  updateGrantOfRepresentation: any;
  updateProofOfBankAccount: any;
  onContinue: any;
  onSectionClick: (section: string) => void;
  updateTemplate: UpdateFormTemplateProc;
  removeRemoteError: () => void;
  showAGOROptional: boolean;
}

export const validateRecord = (
  record: DocumentsRecord,
  askForProofOfAddress: boolean,
  askForWillAndProbate: boolean,
  askForProofOfBankAccount: boolean,
  askForGrantOfRepresentation: boolean
): DocumentErrors | undefined => {
  const errors = {} as DocumentErrors;

  if (record.proofOfDeath.files.length === 0 && record.proofOfDeath.currentIds.length === 0) {
    errors.proofOfDeath = "Required";
  }

  if (
    askForProofOfAddress &&
    record.proofOfAddress.files.length === 0 &&
    record.proofOfAddress.currentIds.length === 0
  ) {
    errors.proofOfAddress = "Required";
  }

  if (
    askForWillAndProbate &&
    record.willAndProbate.files.length === 0 &&
    record.willAndProbate.currentIds.length === 0
  ) {
    errors.willAndProbate = "Required";
  }

  if (
    askForProofOfBankAccount &&
    record.proofOfBankAccount.files.length === 0 &&
    record.proofOfBankAccount.currentIds.length === 0
  ) {
    errors.proofOfBankAccount = "Required";
  }

  if (
    askForGrantOfRepresentation &&
    record.grantOfRepresentation.files.length === 0 &&
    record.grantOfRepresentation.currentIds.length === 0
  ) {
    errors.grantOfRepresentation = "Required";
  }

  return Object.keys(errors).length === 0 ? undefined : errors;
};
type FormErrors = {
  proofOfDeathIds?: string;
  proofOfAddressIds?: string;
  willAndProbateIds?: string;
  proofOfBankAccountIds?: string;
  grantOfRepresentationIds?: string;
};

const validate = (
  { documents }: NonExpressFormData,
  isProofOfAddressNeeded: boolean,
  askForWillAndProbate: boolean,
  askForProofOfBankAccount: boolean,
  askForGrantOfRepresentation: boolean
): FormErrors | undefined => {
  const errors = {} as FormErrors;

  if (documents?.proofOfDeathIds && documents.proofOfDeathIds.length === 0) {
    errors.proofOfDeathIds = "Required field";
  }

  if (
    isProofOfAddressNeeded &&
    documents?.proofOfAddressIds &&
    documents.proofOfAddressIds.length === 0
  ) {
    errors.proofOfAddressIds = "Required field";
  }

  if (
    askForWillAndProbate &&
    documents?.willAndProbateIds &&
    documents.willAndProbateIds.length === 0
  ) {
    errors.willAndProbateIds = "Required field";
  }

  if (
    askForProofOfBankAccount &&
    documents?.proofOfBankAccountIds &&
    documents.proofOfBankAccountIds.length === 0
  ) {
    errors.proofOfBankAccountIds = "Required field";
  }

  if (
    askForGrantOfRepresentation &&
    documents?.grantOfRepresentationIds &&
    documents.grantOfRepresentationIds.length === 0
  ) {
    errors.grantOfRepresentationIds = "Required field";
  }

  return Object.keys(errors).length === 0 ? undefined : errors;
};

export const isDocumentsComplete = (
  form: NonExpressFormData,
  isProofOfAddressNeeded: boolean,
  askForWillAndProbate: boolean,
  askForProofOfBankAccount: boolean,
  askForGrantOfRepresentation: boolean
): boolean => {
  return (
    validate(
      form,
      isProofOfAddressNeeded,
      askForWillAndProbate,
      askForProofOfBankAccount,
      askForGrantOfRepresentation
    ) === undefined
  );
};
export function isClientFile(tag: InternalFileTags | FileTags): boolean {
  if (Object.values(InternalFileTags).includes(tag as InternalFileTags)) return false;
  return true;
}
